import { createSelector } from 'reselect';
import type { FieldSearchDescriptor, FieldSearchIndex } from '../requesters/uapiFieldsRequester/uapiFieldsRequester';
import { resolveOperationIds } from '../utils/operationUtils';
import type { OperationCurrent } from '../utils/operationUtils';
import type { FieldsState } from '../reducers/fieldsReducer';

// TODO: Type state params for legacy selectors once AppState includes fields/operations
// TODO: Move logic of current field here, https://climate.jira.com/browse/CFVWEB-1900
export const currentFieldSelector = (state) => state.field;

// TODO: Consolidate with resolveOperationIds from operationUtils
export const currentOperationsSelector = (state) => {
  const { current } = state.operations;
  const operations = Array.isArray(current) ?
    current.map(operation => operation.operationId) :
    [current];
  return operations;
};

export const accessibleFieldsSelector = (state) => {
  const { byId: byIdOperations, current } = state.operations;
  const { allIds, byId: byIdFields } = state.fields;
  let fields = [];
  if (typeof current === 'string' || current === null) {
    const operationSelected =
      current && byIdOperations[current];
    if (current) {
      fields = allIds.filter((fieldId) => operationSelected && operationSelected.fields.includes(fieldId));
    } else {
      fields = allIds;
    }
    return fields.map((field) => byIdFields[field]);
  }

  let multiOperations: Array<{ fields: string[] }> = [];

  if (current[0] === 'all-operations') {
    multiOperations = Object.values(byIdOperations);
  } else {
    multiOperations = current.map((operationId) => byIdOperations[operationId]);
  }

  const multiOperationsFieldSet = new Set<string>();
  multiOperations.forEach((operation) => {
    if (operation?.fields) {
      operation.fields.forEach((f: string) => multiOperationsFieldSet.add(f));
    }
  });
  fields = allIds.filter((fieldId) => multiOperationsFieldSet.has(fieldId));
  return fields.map((field) => byIdFields[field]);
};

const DROPDOWN_DISPLAY_LIMIT = 200;

interface FieldsSelectorsState {
  fields: Pick<FieldsState, 'searchIndex' | 'nameSearchResults' | 'nameSearchTerm'>;
  operations: { current: OperationCurrent };
}

const getSearchIndex = (state: FieldsSelectorsState): FieldSearchIndex | null => state.fields.searchIndex;
const getNameSearchResults = (state: FieldsSelectorsState): FieldSearchIndex | null => state.fields.nameSearchResults;
const getNameSearchTerm = (state: FieldsSelectorsState): string => state.fields.nameSearchTerm || '';
const getOperationsCurrent = (state: FieldsSelectorsState): OperationCurrent => state.operations.current;

export const fieldSearchIndexSelector = createSelector(
  [getSearchIndex, getNameSearchResults, getNameSearchTerm, getOperationsCurrent],
  (searchIndex, nameSearchResults, nameSearchTerm, current): FieldSearchDescriptor[] => {
    const source = nameSearchResults ?? searchIndex;
    if (!source) return [];

    const currentIds = resolveOperationIds(current);
    const entries = Object.values<FieldSearchDescriptor>(source);
    const trimmedTerm = nameSearchTerm.trim().toLowerCase();

    let filtered: FieldSearchDescriptor[];
    if (currentIds) {
      const idSet = new Set(currentIds);
      filtered = entries.filter(f => idSet.has(f.operationId));
    } else {
      filtered = entries;
    }

    if (trimmedTerm.length > 0) {
      filtered = filtered.filter(f => f.name.toLowerCase().includes(trimmedTerm));
    }

    filtered.sort((a, b) => a.name.localeCompare(b.name));

    if (!nameSearchResults && trimmedTerm.length === 0 && filtered.length > DROPDOWN_DISPLAY_LIMIT) {
      return filtered.slice(0, DROPDOWN_DISPLAY_LIMIT);
    }

    return filtered;
  }
);
